<template>
  <!--
    Spec 6.1, decision 15 (#1693): the check the APPROVING device makes before
    it will wrap the master key to anybody.

    A fragment inside screen 3, which is already an `ion-page`. It performs
    nothing — no camera, no comparison, no crypto. The tap goes out as an
    event, `approval-scan.js` decides, and the answer comes back as `state`.
    That is what lets the refusal be tested without a component and the screen
    be tested without a camera.

    THE ORDER ON SCREEN IS THE ORDER OF PREFERENCE: scan, then type, then — a
    long way down, behind a confirm — approve a phone that is too old to show a
    code at all. Never a silent downgrade: a member who ends up on the last of
    those is told, in a sentence that stays on screen for as long as Approve is
    enabled.
  -->
  <div class="mt-6 flex w-full flex-col items-start">
    <p class="text-[0.9375rem] leading-[1.375rem] text-[var(--courses-ink-muted)]">
      {{ COPY.together }}
    </p>

    <!--
      Hidden outright on a build with no camera — the web harness — rather than
      shown and made to fail on the tap. The typed path below is then the only
      one offered, and it is offered open rather than behind a link.
    -->
    <button
      v-if="scanAvailable"
      type="button"
      data-testid="approval-scan"
      class="mt-4 w-full rounded-full border-[1.5px] border-[var(--courses-border)] bg-[var(--courses-surface-raised)] py-3 text-[1.0625rem] font-extrabold text-[var(--courses-ink)] disabled:opacity-50"
      :disabled="scanning"
      @click="$emit('scan')"
    >
      {{ scanning ? COPY.scanning : COPY.scan }}
    </button>

    <button
      v-if="scanAvailable && !typing"
      type="button"
      data-testid="approval-type-instead"
      class="mt-3 text-[0.9375rem] font-bold underline text-[var(--courses-ink)]"
      @click="reveal"
    >
      {{ COPY.typeInstead }}
    </button>

    <!--
      ⚠️ A FORM, AND A CHECK THAT RUNS BY ITSELF. Reported from an iPhone: type
      the code, tap "Check the code", nothing. On iOS a button tapped while the
      software keyboard is up inside an ion-modal routinely loses its click —
      the tap blurs the input, the keyboard starts to dismiss, the sheet re-lays
      out, and the touch ends over something else. The web build never sees it,
      which is why every test passed.

      So the tap is no longer the way in. The check runs the moment sixteen
      valid characters are present (the watcher below), the keyboard's Done key
      submits through a real form rather than a keyup listener, and the button
      stays as the third way. Whichever fires, the same event goes out.
    -->
    <form
      v-if="typedOpen"
      class="mt-4 flex w-full flex-col items-start"
      novalidate
      @submit.prevent="submit"
    >
      <label :for="inputId" class="text-[0.8125rem] font-bold text-[var(--courses-ink-muted)]">{{
        COPY.codeLabel
      }}</label>
      <!--
        `data-compact-input` is required, not decorative: the blanket
        `input[type='text']:not([data-compact-input])` rule in styles/main.scss
        forces `#ffffff !important`, which in dark mode puts `--courses-ink` on
        white and hides what the member is typing (EnterRecoveryCode.vue hit
        this first).

        Autocorrect off in all three of its spellings, because every one of
        them has turned a Crockford code into a word on some keyboard;
        `normaliseTypedCode` then forgives case, spaces and hyphens and reads
        O, I and L as 0 and 1, so what the member sees on the other screen can
        be typed however it comes out.
      -->
      <input
        :id="inputId"
        v-model="typed"
        data-testid="approval-code-input"
        type="text"
        data-compact-input
        inputmode="text"
        autocomplete="off"
        autocapitalize="characters"
        autocorrect="off"
        spellcheck="false"
        enterkeyhint="done"
        :maxlength="INPUT_MAX_LENGTH"
        class="mt-2 w-full appearance-none rounded-xl border-[1.5px] border-[var(--courses-border)] bg-[var(--courses-surface-raised)] px-3 py-3 text-center font-mono text-[1.0625rem] font-bold uppercase tracking-[0.15em] text-[var(--courses-ink)] shadow-none"
      />
      <button
        type="submit"
        data-testid="approval-submit-code"
        class="mt-3 w-full rounded-full border-[1.5px] border-[var(--courses-border)] bg-[var(--courses-surface-raised)] py-3 text-[1.0625rem] font-extrabold text-[var(--courses-ink)]"
        @click="submit"
      >
        {{ COPY.checkCode }}
      </button>
    </form>

    <!--
      What the last check said, for everything that is not the refusal. Always
      in the tree so the region announces a CHANGE rather than being inserted:
      a screen reader may say nothing at all about a region that has only just
      appeared, and the member who cannot see the screen is exactly the one who
      needs to hear that a code matched.
    -->
    <div data-testid="approval-check-state" role="status" aria-live="polite" class="w-full">
      <p v-if="message" class="mt-4 text-[0.9375rem] leading-[1.375rem] text-[var(--courses-ink)]">
        {{ message }}
      </p>
    </div>

    <!--
      The refusal, and the strongest sentence on either screen. `role="alert"`
      rather than the polite region above, because this is the one outcome that
      must interrupt: the member is about to hand over the key that opens
      everything they have written.

      The sentence comes from the CHECK, never from `courses-devices`
      state.error — nothing was sent, so there is no API error to render, and
      that is the whole point of the criterion. Approve stays disabled behind
      it.
    -->
    <div
      v-if="state === APPROVAL_CHECK.REFUSED_KEY"
      data-testid="approval-refused"
      role="alert"
      class="mt-4 w-full rounded-2xl bg-[var(--courses-surface-sunken)] px-4 py-3"
    >
      <p class="text-[0.9375rem] leading-[1.375rem] font-bold text-[var(--courses-ink)]">
        {{ COPY.refusedKey }}
      </p>
    </div>

    <!--
      The v: 1 fallback, for the phone that is on a build with no code to show.
      A link, then a confirm, then a standing sentence — never one tap, and
      never silent. #1690's transition is over once every pending device can
      show a code, and this is the lever that has to come out then: a follow-up
      to remove it lands with Phase 3.
    -->
    <template v-if="state !== APPROVAL_CHECK.OLDER_VERSION">
      <button
        v-if="!confirming"
        type="button"
        data-testid="approval-older-version"
        class="mt-6 text-[0.875rem] underline text-[var(--courses-ink-subtle)]"
        @click="confirming = true"
      >
        {{ COPY.olderVersion }}
      </button>
      <div
        v-else
        data-testid="approval-older-version-confirm"
        class="mt-6 w-full rounded-2xl bg-[var(--courses-surface-sunken)] px-4 py-3"
      >
        <p class="text-[0.9375rem] leading-[1.375rem] text-[var(--courses-ink)]">
          {{ COPY.olderVersionQuestion }}
        </p>
        <div class="mt-3 flex flex-wrap items-center gap-4">
          <button
            type="button"
            data-testid="approval-older-version-confirm-yes"
            class="text-[0.9375rem] font-bold underline text-[var(--courses-ink)]"
            @click="acceptOlderVersion"
          >
            {{ COPY.olderVersionConfirm }}
          </button>
          <button
            type="button"
            data-testid="approval-older-version-confirm-no"
            class="text-[0.9375rem] font-bold underline text-[var(--courses-ink-muted)]"
            @click="confirming = false"
          >
            {{ COPY.olderVersionCancel }}
          </button>
        </div>
      </div>
    </template>
    <p
      v-else
      data-testid="approval-older-version-standing"
      class="mt-6 w-full text-[0.9375rem] leading-[1.375rem] font-bold text-[var(--courses-ink)]"
    >
      {{ COPY.olderVersionStanding }}
    </p>
  </div>
</template>

<script>
import { TOGETHER_SENTENCE } from '@/pages/courses/encryption/approval-copy';
import { TYPED_CODE_LENGTH } from '@/utils/courses-crypto/approval-secret';
import { APPROVAL_CHECK } from '@/utils/courses-encryption/approval-scan';

/**
 * Sixteen characters, plus room for the separators a member copies across with
 * them. `normaliseTypedCode` strips those, so the cap is on TYPING rather than
 * on the code: an input that stopped at sixteen would refuse the last four
 * characters of `ABCD-EFGH-JKMN-PQRS`, which is the form the other phone shows.
 */
export const INPUT_MAX_LENGTH = 24;

/** Per-instance, so a label always names the input beside it and never another one. */
let nextInputId = 0;

/** Spec 6.7, screen 3, Phase 2b. Drafted; flagged on #1667 for the product call. */
export const COPY = {
  together: TOGETHER_SENTENCE,
  scan: 'Scan the code on that phone',
  scanning: 'Scanning…',
  typeInstead: 'Type the code instead',
  codeLabel: 'The code shown on the other phone',
  checkCode: 'Check the code',
  matched: 'The code matches the phone that asked. You can approve it.',
  /*
   * ⚠️ "ADDS UP", NOT "ACCEPTED". The check character has caught a typo, and
   * that is all this phone can check: a typed code carries no public key to
   * compare (`verifyTypedCode` says why that is the design, not a gap). The
   * sentence that used to stand here claimed acceptance of a code nothing had
   * checked, and a member who mistyped read it as the app agreeing with them.
   * What is still not known — whether this is the code of the phone that
   * asked — is decided on that phone, after Approve, which says so and can ask
   * again.
   */
  typed:
    'Code entered, and it adds up. If it is still not this phone’s code, that phone will say so once you approve, and can ask again.',
  /*
   * The check character's sentence (`approval-secret.js`). It cannot say WHICH
   * character — the check knows the sixteen disagree, not which to blame — so
   * it says what to do, which is read it again.
   */
  checkFailed:
    'One character of that code is wrong. Read it again on the other phone and try again.',
  wrongDevice:
    'That code belongs to a different phone. Scan the code on the phone that is asking to join.',
  malformed: 'That is not an approval code. Check it on the other phone and try again.',
  unavailable: 'The camera could not be opened, so the code has to be typed instead.',
  /**
   * The strongest sentence on either screen, and it says what to DO. A member
   * who is only told something does not match will try again; the one thing
   * they must not do is keep tapping until it works.
   */
  refusedKey:
    'Do not approve this. The code on that phone does not match the phone that asked to join — which is what someone else trying to reach your notes would look like. Tap Deny.',
  olderVersion: 'That phone is on an older version and shows no code',
  olderVersionQuestion:
    'Approve without the code? Do this only if the phone asking to join is in your hand and shows no code at all.',
  olderVersionConfirm: 'Approve without the code',
  olderVersionCancel: 'Go back',
  olderVersionStanding:
    'You are approving without the code. Nothing but the two phones being in the same room protects this one.',
};

/** One sentence per outcome; the states with no sentence say nothing. */
const MESSAGES = Object.freeze({
  [APPROVAL_CHECK.MATCHED]: COPY.matched,
  [APPROVAL_CHECK.TYPED]: COPY.typed,
  [APPROVAL_CHECK.WRONG_DEVICE]: COPY.wrongDevice,
  [APPROVAL_CHECK.MALFORMED]: COPY.malformed,
  [APPROVAL_CHECK.CHECK_FAILED]: COPY.checkFailed,
  [APPROVAL_CHECK.UNAVAILABLE]: COPY.unavailable,
});

const STATES = Object.values(APPROVAL_CHECK);

export default {
  name: 'CoursesApprovalCheck',
  props: {
    /** One of `APPROVAL_CHECK`: what the caller's check is doing, or decided. */
    state: {
      type: String,
      default: APPROVAL_CHECK.IDLE,
      validator: value => STATES.includes(value),
    },
    /**
     * Whether this build can open a camera at all. False on the web harness,
     * and then the typed path is the only one offered — not a Scan button that
     * fails when it is tapped.
     */
    scanAvailable: { type: Boolean, default: false },
  },
  emits: ['scan', 'submit-code', 'older-version', 'reset'],
  data() {
    return {
      /** Unique per instance: two of these on one page would otherwise share a label. */
      inputId: `approval-code-${(nextInputId += 1)}`,
      /** The member asked for the typed form. Irrelevant with no camera. */
      typing: false,
      /** Exactly what they typed; the caller normalises it. */
      typed: '',
      /** The older-version link has been tapped and is asking. */
      confirming: false,
      /** The sixteen characters the watcher last sent, so an unchanged code is not re-checked on every keystroke. */
      lastAutoChecked: '',
    };
  },
  watch: {
    /**
     * Check as soon as there is something to check. Sixteen valid characters
     * is what `normaliseTypedCode` needs, so that is the moment; the value goes
     * out exactly as typed, separators and all, the way the button sends it.
     * Only when the sixteen CHANGE: a member who corrects one character gets a
     * fresh check, and one who has not is not shown the same sentence twice.
     */
    typed(value) {
      const characters = value.toUpperCase().replace(/[^0-9A-Z]/g, '');
      if (characters.length !== TYPED_CODE_LENGTH || characters === this.lastAutoChecked) return;
      this.lastAutoChecked = characters;
      this.$emit('submit-code', value);
    },
  },
  computed: {
    COPY: () => COPY,
    APPROVAL_CHECK: () => APPROVAL_CHECK,
    INPUT_MAX_LENGTH: () => INPUT_MAX_LENGTH,
    scanning() {
      return this.state === APPROVAL_CHECK.SCANNING;
    },
    /** Open on request, and open from the start when there is no camera to offer. */
    typedOpen() {
      return this.typing || !this.scanAvailable;
    },
    message() {
      return MESSAGES[this.state] ?? null;
    },
  },
  methods: {
    /**
     * Opening the typed form clears whatever the last scan decided: leaving a
     * refusal or a "wrong phone" sitting above a fresh attempt reads as if it
     * applied to what is being typed now.
     */
    reveal() {
      this.typing = true;
      this.$emit('reset');
    },
    submit() {
      this.$emit('submit-code', this.typed);
    },
    acceptOlderVersion() {
      this.confirming = false;
      this.$emit('older-version');
    },
  },
};
</script>
